import * as Api from '@ViewModels';
import { getSocialMediaPlainTextContentFromRawRichContent } from './richContentExtractionUtils';
import { createContentStateWithHtmlStringValue } from './richContentUtils';
import { applyFacebookMentions, parseFacebookMentions } from './socialUtils';

/**
 * A platform's version while it is being written. The composer edits plain text the way the base
 * post does; the rich-text document is built when the post is sent.
 *
 * Once the post is split a version owns its media outright: an upload, crop, or removal on one
 * platform's tab changes that version alone, and no other tab shows it.
 */
export interface IPlatformCustomizationDraft {
	platform: Api.SocialMediaType;
	content: string;
	attachments: Api.IFileAttachment[];
}

export const findPlatformCustomization = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	platform: Api.SocialMediaType
) => customizations?.find(customization => customization.platform === platform);

export const createCustomizationFromBase = (
	platform: Api.SocialMediaType,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
): IPlatformCustomizationDraft => ({
	platform,
	content: baseContent ?? '',
	attachments: [...(baseAttachments ?? [])],
});

export const getTargetedPlatforms = (postTargets?: readonly Api.IPostTarget[]): Api.SocialMediaType[] => {
	const providers = (postTargets ?? [])
		.map(target => target.provider)
		.filter(provider => Api.SupportedSocialMediaTypes.includes(provider));
	return Api.SupportedSocialMediaTypes.filter(platform => providers.includes(platform));
};

/** Customized with nothing targeted, the post is still one post, and the composer edits the base. */
export const isSplitByPlatform = (
	customizeByPlatform: boolean | undefined,
	postTargets: readonly Api.IPostTarget[] | undefined
) => !!customizeByPlatform && getTargetedPlatforms(postTargets).length > 0;

/**
 * The version the composer is editing. A stored selection only counts while its platform is still
 * targeted — untargeting the platform on screen must not leave edits pointed at a version that is
 * no longer sent — so it otherwise falls back to the first targeted platform, which is the tab the
 * version tabs select. Undefined when nothing is targeted.
 */
export const getActivePlatform = (
	postTargets: readonly Api.IPostTarget[] | undefined,
	selectedPlatform: Api.SocialMediaType | undefined
): Api.SocialMediaType | undefined => {
	const targetedPlatforms = getTargetedPlatforms(postTargets);
	return selectedPlatform && targetedPlatforms.includes(selectedPlatform) ? selectedPlatform : targetedPlatforms[0];
};

/**
 * The version a media edit lands on: the tab on screen while the post is split, by the rule the tabs
 * use. Undefined while the post is one post, customized or not, which is when the base is edited.
 */
export const getEditedPlatform = (
	customizeByPlatform: boolean | undefined,
	postTargets: readonly Api.IPostTarget[] | undefined,
	selectedPlatform: Api.SocialMediaType | undefined
): Api.SocialMediaType | undefined =>
	customizeByPlatform ? getActivePlatform(postTargets, selectedPlatform) : undefined;

/**
 * Every targeted platform ends up with a complete entry, copied from the base where one is missing —
 * the server rejects a gap rather than falling back to the base, so the client fills it here.
 *
 * Entries for platforms that are no longer targeted are kept in the draft and simply not sent, so
 * untargeting a platform and targeting it again in the same session does not lose its wording.
 */
export const materializePlatformCustomizations = (
	targetedPlatforms: readonly Api.SocialMediaType[],
	existing: readonly IPlatformCustomizationDraft[] | undefined,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
): IPlatformCustomizationDraft[] => {
	const missing = targetedPlatforms.filter(platform => !findPlatformCustomization(existing, platform));
	if (!missing.length) {
		return [...(existing ?? [])];
	}
	return [
		...(existing ?? []),
		...missing.map(platform => createCustomizationFromBase(platform, baseContent, baseAttachments)),
	];
};

const hasSameAttachments = (
	attachments: readonly Api.IFileAttachment[],
	baseAttachments: readonly Api.IFileAttachment[] | undefined
) => {
	const baseIds = (baseAttachments ?? []).map(attachment => attachment.id);
	return (
		attachments.length === baseIds.length && attachments.every((attachment, index) => attachment.id === baseIds[index])
	);
};

/**
 * One attachment can appear more than once in the same media, as a picture picked twice from the image
 * library does, so the helpers below count copies rather than compare sets of ids.
 */
const countById = (media: readonly Api.IFileAttachment[]) => {
	const counts = new Map<string, number>();
	media.forEach(attachment => counts.set(attachment.id, (counts.get(attachment.id) ?? 0) + 1));
	return counts;
};

/**
 * The copies in `media` that `other` has no copy to match, in order. With `other` holding an attachment
 * twice, its first two copies here are matched and a third is left over.
 */
export const getUnmatchedMedia = (media: readonly Api.IFileAttachment[], other: readonly Api.IFileAttachment[]) => {
	const unmatchedCounts = countById(other);
	return media.filter(attachment => {
		const matches = unmatchedCounts.get(attachment.id) ?? 0;
		if (matches > 0) {
			unmatchedCounts.set(attachment.id, matches - 1);
			return false;
		}
		return true;
	});
};

/** Which original a crop or edit stands in for: replacement id → the id it replaced, resolved to the root. */
export type IMediaReplacements = Readonly<Record<string, string>>;

const findOriginalId = (replacements: IMediaReplacements, id: string) => {
	let original = id;
	// Bounded by the map's size, so a cycle from bad input cannot loop forever.
	for (let step = 0; step < Object.keys(replacements).length && replacements[original]; step++) {
		original = replacements[original];
	}
	return original;
};

/** How many copies of each original the media holds, itself or through a crop or edit of it. */
const countOriginals = (media: readonly Api.IFileAttachment[], replacements: IMediaReplacements) => {
	const counts = new Map<string, number>();
	media.forEach(attachment => {
		const originalId = findOriginalId(replacements, attachment.id);
		counts.set(originalId, (counts.get(originalId) ?? 0) + 1);
	});
	return counts;
};

/**
 * Records what a change to one version's media replaced: each copy it added stands in for the copy it
 * removed at the same turn, in order, as a crop, an AI edit, or a rebrand does. Removals with nothing
 * added in their place are plain removals, and putting an original back, as undoing a crop does,
 * replaces nothing.
 */
export const trackReplacements = (
	replacements: IMediaReplacements,
	before: readonly Api.IFileAttachment[],
	after: readonly Api.IFileAttachment[]
): IMediaReplacements => {
	const removed = getUnmatchedMedia(before, after);
	const added = getUnmatchedMedia(after, before);
	if (!removed.length || !added.length) {
		return replacements;
	}
	const next = { ...replacements };
	added.slice(0, removed.length).forEach((attachment, index) => {
		const originalId = findOriginalId(replacements, removed[index].id);
		if (originalId !== attachment.id) {
			next[attachment.id] = originalId;
		}
	});
	return next;
};

/**
 * The uploads the base keeps once a version gains `additions`: enough copies of each that the starting
 * media and the uploads together hold as many as the version does, so the base never counts fewer than
 * a version publishes.
 */
export const recordUploads = (
	uploads: readonly Api.IFileAttachment[],
	seedAttachments: readonly Api.IFileAttachment[],
	version: readonly Api.IFileAttachment[],
	additions: readonly Api.IFileAttachment[],
	replacements: IMediaReplacements = {}
): Api.IFileAttachment[] => {
	const versionCounts = countOriginals(version, replacements);
	const baseCounts = countById([...seedAttachments, ...uploads]);
	const recorded = [...uploads];
	additions.forEach(addition => {
		const baseCount = baseCounts.get(addition.id) ?? 0;
		if (baseCount < (versionCounts.get(addition.id) ?? 0)) {
			recorded.push(addition);
			baseCounts.set(addition.id, baseCount + 1);
		}
	});
	return recorded;
};

/**
 * The base post's media while split, worked out afresh after every change: the starting media and the
 * uploads that a targeted version still holds, itself or through a crop or edit of it, as many copies of
 * each as the version holding the most. The API counts the base toward the post-wide limits, so an
 * upload leaves it once no targeted tab holds it and comes back if one does again. Crops and edits never
 * join it, so turning the split off brings back their originals instead.
 *
 * It never goes empty while a targeted version holds media, since the API checks Instagram's
 * requirement on the base: it keeps one original there, or failing that one of the version's own.
 */
export const reconcileBaseMedia = (
	seedAttachments: readonly Api.IFileAttachment[],
	uploads: readonly Api.IFileAttachment[],
	customizations: readonly IPlatformCustomizationDraft[],
	targetedPlatforms: readonly Api.SocialMediaType[],
	replacements: IMediaReplacements = {}
): Api.IFileAttachment[] => {
	const heldCounts = new Map<string, number>();
	let firstHeld: Api.IFileAttachment | undefined;
	targetedPlatforms.forEach(platform => {
		const attachments = findPlatformCustomization(customizations, platform)?.attachments ?? [];
		firstHeld ??= attachments[0];
		countOriginals(attachments, replacements).forEach((count, id) =>
			heldCounts.set(id, Math.max(heldCounts.get(id) ?? 0, count))
		);
	});
	const originals = [...seedAttachments, ...uploads];
	const next = originals.filter(attachment => {
		const count = heldCounts.get(attachment.id) ?? 0;
		if (!count) {
			return false;
		}
		heldCounts.set(attachment.id, count - 1);
		return true;
	});
	if (next.length || !firstHeld) {
		return next;
	}
	return [originals[0] ?? firstHeld];
};

/** Whether two lists hold the same media in the same order, so a caller can keep the one it has. */
export const isSameMedia = (a: readonly Api.IFileAttachment[], b: readonly Api.IFileAttachment[]) =>
	a.length === b.length && a.every((attachment, index) => attachment.id === b[index].id);

/** Names each copy by its attachment and how many copies of it come before, so copies can be told apart. */
const getCopyKeys = (media: readonly Api.IFileAttachment[]) => {
	const seenCounts = new Map<string, number>();
	return media.map(attachment => {
		const copy = seenCounts.get(attachment.id) ?? 0;
		seenCounts.set(attachment.id, copy + 1);
		return `${attachment.id}#${copy}`;
	});
};

/**
 * Applies a change made to one set of media to another that has diverged from it: the copies the change
 * removed go, the copies it added join unless the other set already had extra copies of their own, and
 * what both share takes the changed order, so a reorder or a crop in place carries over. For edits made
 * while the post is customized but targets nothing, where the composer shows one post and every stored
 * version should follow it.
 */
export const applyMediaChange = (
	attachments: readonly Api.IFileAttachment[],
	before: readonly Api.IFileAttachment[],
	after: readonly Api.IFileAttachment[]
): Api.IFileAttachment[] => {
	const kept = getUnmatchedMedia(attachments, getUnmatchedMedia(before, after));
	const ownExtras = getUnmatchedMedia(kept, before);
	const changed = [...kept, ...getUnmatchedMedia(getUnmatchedMedia(after, before), ownExtras)];
	const afterIndex = new Map(getCopyKeys(after).map((key, index) => [key, index]));
	const changedKeys = getCopyKeys(changed);
	const inChangedOrder = changed
		.map((_, index) => index)
		.filter(index => afterIndex.has(changedKeys[index]))
		.sort((x, y) => afterIndex.get(changedKeys[x]) - afterIndex.get(changedKeys[y]))
		.map(index => changed[index]);
	let next = 0;
	return changed.map((attachment, index) => (afterIndex.has(changedKeys[index]) ? inChangedOrder[next++] : attachment));
};

/** What a split post's base is made of, which the composer keeps while it edits one. */
export interface ISplitMedia {
	seed: Api.IFileAttachment[];
	uploads: Api.IFileAttachment[];
	replacements: IMediaReplacements;
}

/**
 * Reads a stored split post's base back into its parts, since the API keeps only the base. Each version's
 * crops and edits are paired, in order, with the base media it lacks, which they most likely replaced.
 * An uploaded item sits on one tab, so base media two or more versions hold, themselves or through a
 * crop of it, is starting media. The base lists starting media before uploads, so an item one version
 * holds counts as starting media when it comes before the last one known to be. With one version
 * nothing tells them apart, and all of it reads as starting media.
 */
export const inferSplitMedia = (
	baseAttachments: readonly Api.IFileAttachment[],
	customizations: readonly IPlatformCustomizationDraft[]
): ISplitMedia => {
	if (!customizations.length) {
		return { replacements: {}, seed: [...baseAttachments], uploads: [] };
	}
	const baseIds = new Set(baseAttachments.map(attachment => attachment.id));
	const replacements: Record<string, string> = {};
	const representedIds = customizations.map(version => {
		const heldIds = new Set(version.attachments.map(attachment => attachment.id));
		const missing = getUnmatchedMedia(baseAttachments, version.attachments);
		const edits = version.attachments.filter(attachment => !baseIds.has(attachment.id));
		edits.slice(0, missing.length).forEach((edit, index) => {
			replacements[edit.id] = missing[index].id;
			heldIds.add(missing[index].id);
		});
		return heldIds;
	});
	const isSurelySeed = (attachment: Api.IFileAttachment) => {
		const holders = representedIds.filter(ids => ids.has(attachment.id)).length;
		return holders !== 1 || holders === customizations.length;
	};
	const lastSeedIndex = baseAttachments.reduce(
		(last, attachment, index) => (isSurelySeed(attachment) ? index : last),
		-1
	);
	const seed = baseAttachments.filter((attachment, index) => index <= lastSeedIndex);
	return { replacements, seed, uploads: baseAttachments.slice(lastSeedIndex + 1) };
};

export const customizationDiffersFromBase = (
	customization: IPlatformCustomizationDraft | undefined,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
) =>
	!!customization &&
	(customization.content !== (baseContent ?? '') || !hasSameAttachments(customization.attachments, baseAttachments));

/** What going back to one post takes from a platform: its own wording, its own pick of media, or both. */
export interface IPlatformVersionChange {
	platform: Api.SocialMediaType;
	wording: boolean;
	media: boolean;
}

/**
 * Every version the draft holds that differs from the base post, targeted or not: turning the split off
 * discards all of them, including one kept for a platform that was untargeted along the way.
 */
export const getPlatformVersionChanges = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
): IPlatformVersionChange[] =>
	Api.SupportedSocialMediaTypes.flatMap(platform => {
		const customization = findPlatformCustomization(customizations, platform);
		if (!customization) {
			return [];
		}
		const wording = customization.content !== (baseContent ?? '');
		const media = !hasSameAttachments(customization.attachments, baseAttachments);
		return wording || media ? [{ media, platform, wording }] : [];
	});

/**
 * True while a targeted platform's version leaves out some of the base post's media. Going back to one
 * post publishes all of it everywhere, so an upload made on one tab reaches every platform.
 */
export const isMediaLeftOffAPlatform = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	baseAttachments: readonly Api.IFileAttachment[],
	targetedPlatforms: readonly Api.SocialMediaType[]
) =>
	targetedPlatforms.some(platform => {
		const customization = findPlatformCustomization(customizations, platform);
		return !!customization && getUnmatchedMedia(baseAttachments ?? [], customization.attachments).length > 0;
	});

/**
 * Every attachment the post publishes somewhere: the base while it is one post, each targeted
 * version's own media once it is split. Versions copied from the base share attachments, so each
 * appears once.
 */
export const getPublishedAttachments = (
	isSplit: boolean,
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	targetedPlatforms: readonly Api.SocialMediaType[],
	baseAttachments: readonly Api.IFileAttachment[] | undefined
): Api.IFileAttachment[] => {
	if (!isSplit) {
		return [...(baseAttachments ?? [])];
	}
	const published = new Map<string, Api.IFileAttachment>();
	targetedPlatforms.forEach(platform => {
		findPlatformCustomization(customizations, platform)?.attachments.forEach(attachment => {
			if (!published.has(attachment.id)) {
				published.set(attachment.id, attachment);
			}
		});
	});
	return [...published.values()];
};

/** The composer context fields that say what a post publishes as words. */
export interface IPublishedContentSource {
	customizeByPlatform?: boolean;
	platformCustomizations?: readonly IPlatformCustomizationDraft[];
	postContent?: string;
	postTargets?: readonly Api.IPostTarget[];
}

/**
 * The words the post publishes: the base's while it is one post, each targeted version's once it is
 * split, when the base goes nowhere. A targeted platform with no version reads as an empty string.
 */
export const getPublishedContent = ({
	customizeByPlatform,
	platformCustomizations,
	postContent,
	postTargets,
}: IPublishedContentSource): string[] => {
	if (!isSplitByPlatform(customizeByPlatform, postTargets)) {
		return [postContent ?? ''];
	}
	return getTargetedPlatforms(postTargets).map(
		platform => findPlatformCustomization(platformCustomizations, platform)?.content ?? ''
	);
};

/**
 * Whether the post has words to publish somewhere. The save and schedule controls read this rather
 * than the base, which stays empty when the author splits the post before writing it. Judged on the
 * trimmed text, the way the per-platform validation and the API judge a version.
 */
export const hasPublishedContent = (post: IPublishedContentSource): boolean =>
	getPublishedContent(post).some(content => content.trim().length > 0);

export const isFacebookTargeted = (postTargets?: readonly Api.IPostTarget[]) =>
	getTargetedPlatforms(postTargets).includes(Api.SocialMediaType.Facebook);

/**
 * Only the platforms passed are sent, in their order, and only those with an entry.
 *
 * Every version is written and stored like the base body, mentions included: each carries its own
 * "@Page Name" text and each is tokenized against the same picked Pages. A version publishes to one
 * platform, so a Page mention in one cannot reach a network that would only strip it.
 */
export const toApiPlatformCustomizations = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	targetedPlatforms: readonly Api.SocialMediaType[],
	facebookMentions?: readonly Api.IFacebookPageLookupResult[]
): Api.IPostPlatformCustomization[] =>
	targetedPlatforms
		.map(platform => findPlatformCustomization(customizations, platform))
		.filter((customization): customization is IPlatformCustomizationDraft => !!customization)
		.map(customization => ({
			platform: customization.platform,
			content: {
				document: createContentStateWithHtmlStringValue(
					applyFacebookMentions(customization.content, facebookMentions ? [...facebookMentions] : [])
				).getRawRichTextContent().document,
			},
			attachments: customization.attachments,
		}));

/**
 * Stored versions as drafts, with every Page their tokens name. A version can mention a Page the base
 * never does, and a Page missing from the composer's picked set is saved back as plain text.
 */
export const fromApiPlatformCustomizations = (
	customizations: readonly Api.IPostPlatformCustomization[] | undefined
): { drafts: IPlatformCustomizationDraft[]; mentions: Api.IFacebookPageLookupResult[] } => {
	const mentions: Api.IFacebookPageLookupResult[] = [];
	const drafts = (customizations ?? []).map(customization => {
		const parsed = parseFacebookMentions(getSocialMediaPlainTextContentFromRawRichContent(customization.content));
		mentions.push(...parsed.mentions);
		return {
			platform: customization.platform,
			content: parsed.content,
			attachments: customization.attachments ?? [],
		};
	});
	return { drafts, mentions };
};

/**
 * What one platform publishes from a stored post or template, for read-only previews: its own version
 * once the source is split, the base otherwise. A split source with no version for the platform
 * publishes nothing there — the server fails that target rather than falling back — so it previews
 * empty instead of showing base copy that never goes out. Generic over the platform type because the
 * anonymous compliance view declares its own copy of the platform enum.
 */
export const getPublishedPlatformVersion = <TPlatform, TAttachment>(
	source:
		| {
				content?: Api.IRawRichTextContentState;
				attachments?: TAttachment[];
				platformCustomizations?: readonly {
					platform: TPlatform;
					content?: Api.IRawRichTextContentState;
					attachments?: TAttachment[];
				}[];
		  }
		| null
		| undefined,
	platform: TPlatform | undefined
): { content?: Api.IRawRichTextContentState; attachments: TAttachment[] } => {
	if (!source?.platformCustomizations?.length) {
		return { content: source?.content, attachments: source?.attachments ?? [] };
	}
	const version = source.platformCustomizations.find(customization => customization.platform === platform);
	return { content: version?.content, attachments: version?.attachments ?? [] };
};
